const THDCustomer = require('@thd-olt-global/thd-customer');

const headerUtility = require('header.utility');

const {
  shared: {
    support: {
      eliteCallCenterNumber: defaultPhoneNumber,
      eliteCallCenterSMSCode: defaultShortCode,
    },
  },
} = require('../../data/shared.json');

/**
 * Each of the elementIDs corresponds to a real id assigned
 * to one of the elements in the HF markup. You can search
 * the mustache template files for any given elementId to find the
 * corresponding element.
 */
const dynamicPhoneNumberElements = {
  /**
   * This is the phone number in the upper lefthand corner
   * of the header.
   *
   * For this one, we decided not to render the default phone
   * number initially, and only to render a phone number
   * when we have one ready (which might still end up being
   * the default phone number, anyway, so you shouldn't necessarily
   * expect a different number when it does render).
   */
  desktopHeaderSupportSection: {
    elementId: 'supportSectionPhoneNumber',

    modifyElementHtml({ phoneNumber, currentElement }) {
      if (currentElement === null) return;
      const pipe = document.createElement('span');
      pipe.classList.add('hf-support__pipe');
      pipe.textContent = '|';

      const dynamicPhoneNumber = document.createElement('span');
      dynamicPhoneNumber.textContent = phoneNumber;

      currentElement.appendChild(pipe);
      currentElement.appendChild(dynamicPhoneNumber);
    },
  },

  /**
   * The footer top bar is the dark gray bar at the top of the footer.
   *
   * This is the phone number on the right side of that top bar.
   */
  desktopFooterTopBar: {
    elementId: 'footerTopBarPhoneNumber',

    modifyElementHtml({ phoneNumber, currentElement }) {
      if (currentElement === null) return;

      currentElement.setAttribute('href', `tel:${phoneNumber}`);
      currentElement.textContent = phoneNumber;
    },
  },

  /**
   * This is the last line of the legal text, which includes
   * a reference to the call center number if anyone using
   * a screen reader is having difficulties with the site.
   *
   * This instance is a great example of a place on the page
   * where we always have to have some kind of phone number
   * present. Otherwise the legal text, and an important
   * accessibility message, would read "Call us at [no phone number]".
   */
  desktopFooterCopyrightText: {
    elementId: 'copyrightTextPhoneNumber',
    modifyElementHtml({ phoneNumber, currentElement }) {
      if (currentElement === null) return;

      currentElement.textContent = phoneNumber;
    },
  },

  /**
   * Note that the phone number is nested within an anchor tag.
   *
   * That's why we need to grab the currentElement's parentNode and
   * set its href, instead of the currentElement's
   */
  mobileFooterCallUsAt: {
    elementId: 'mobileFooterCallUsAtNumber',
    modifyElementHtml({ phoneNumber, currentElement }) {
      if (currentElement === null) return;

      const { parentNode } = currentElement;
      parentNode.setAttribute('href', `tel:${phoneNumber}`);

      currentElement.textContent = phoneNumber;
    },
  },
};

/**
 * Each of the elementIDs corresponds to a real id assigned
 * to one of the elements in the HF markup. You can search
 * the mustache template files for any given elementId to find the
 * corresponding element.
 */
const dynamicShortCodeElements = {
  /**
   * Note that the phone number is nested within an anchor tag.
   *
   * That's why we need to grab the currentElement's parentNode and
   * set its href, instead of the currentElement's
   */
  mobileFooterShortCode: {
    elementId: 'mobileFooterShortCode',
    modifyElementHtml({ shortCode, currentElement }) {
      if (currentElement === null) return;

      const { parentNode } = currentElement;
      parentNode.setAttribute('href', `sms:${shortCode}`);

      currentElement.textContent = shortCode;
    },
  },
};

/**
 * We have a default customer call center number that is included
 * in the pre-built HTML assets that we upload to the CDN.
 *
 * For certain customers, though, we don't want to use that default
 * phone number.
 *
 * This function will check localStorage for a cached phone number.
 * If the cached phone number exists, and if its associated expiration
 * timestamp has not yet passed, and if the cached SVOC ID matches the
 * current user's, then we will replace that default phone number with
 * the cached phone number.
 *
 * Otherwise, we will make a call to a Loyalty endpoint to request
 * the latest phone number for the given customer. The phone number we
 * want to show a given customer is based on that customer's SVOC ID.
 *
 * If we receive a phone number from Loyalty, we'll cache it in localStorage
 * with an expiration timestamp. (If we didn't cache the phone number, we'd
 * be forced to call Loyalty on every page load, which would cause too much
 * stress for their endpoint, and is unnecessary because the phone number
 * won't change that frequently.)
 *
 * @returns {Promise<undefined>} This function returns a Promise so you can chain or await it as needed.
 */
function renderPhoneNumbers(
  dynamicPhoneNumberElements,
  dynamicShortCodeElements,
) {
  const hoursUntilExpiration = 24;
  const localStorageKey = 'b2b-hf-dynamic-phone-number';

  const currentUserSvocId =
    (THDCustomer && THDCustomer.default && THDCustomer.default.svocID) || null;

  const cachedPhoneNumber = getCachedPhoneNumber({
    localStorageKey,
    currentUserSvocId,
  });

  if (cachedPhoneNumber !== null) {
    replacePhoneNumbers({
      dynamicPhoneNumberElements: dynamicPhoneNumberElements,
      dynamicShortCodeElements: dynamicShortCodeElements,
      newPhoneNumber: cachedPhoneNumber.cachedPhoneNumber,
      newShortCode: cachedPhoneNumber.cachedShortCode,
    });
    return Promise.resolve();
  }

  return fetchDynamicPhoneNumber(currentUserSvocId).then(function ({
    newPhoneNumber,
    newShortCode,
    shouldCache,
  }) {
    replacePhoneNumbers({
      dynamicPhoneNumberElements: dynamicPhoneNumberElements,
      dynamicShortCodeElements: dynamicShortCodeElements,
      newPhoneNumber: newPhoneNumber,
      newShortCode: newShortCode,
    });

    if (shouldCache) {
      cachePhoneNumber({
        localStorageKey,
        newPhoneNumber,
        newShortCode,
        hoursUntilExpiration,
        currentUserSvocId,
      });
    }
  });
}

/**
 * Check localStorage for a cached phone number.
 *
 * We will return the cached phone number if the following conditions are met:
 *  1. There is a cached phone number
 *  2. We haven't yet passed the cached expiration timestamp
 *  3. The current user's SVOC ID must match the cached SVOC ID
 *
 * @param {string} localStorageKey The key in localStorage under which the phone number is cached.
 * @param {string} currentUserSvocId The SVOC ID of the current user.
 *
 * @returns {({string,string}|null)} Returns the phone number as a string if it is still valid, otherwise `null`.
 */
function getCachedPhoneNumber({ localStorageKey, currentUserSvocId }) {
  try {
    const {
      phoneNumber: cachedPhoneNumber,
      shortCode: cachedShortCode,
      expiration: expirationTimestamp,
      svocId: cachedSvocId,
    } = JSON.parse(localStorage.getItem(localStorageKey));

    if (
      typeof cachedPhoneNumber !== 'undefined' &&
      typeof cachedShortCode !== 'undefined' &&
      typeof expirationTimestamp !== 'undefined' &&
      typeof cachedSvocId !== 'undefined'
    ) {
      const now = new Date();
      const expiration = new Date(expirationTimestamp);

      if (
        now.getTime() < expiration.getTime() &&
        currentUserSvocId === cachedSvocId
      ) {
        return {
          cachedPhoneNumber: cachedPhoneNumber,
          cachedShortCode: cachedShortCode,
        };
      }
    }
    return null;
  } catch (_) {
    return null;
  }
}

/**
 * Cache in localStorage the given phone number, an expiration timestamp based
 * on @param hoursUntilExpiration, and the current user's SVOC ID.
 *
 * @param {Object} phoneNumberInformation
 * @param {string} phoneNumberInformation.localStorageKey The key to store the information under in localStorage
 * @param {string} phoneNumberInformation.newPhoneNumber A phone number to persist in localStorage
 * @param {string} phoneNumberInformation.newShortCode A text short code to persist in localStorage
 * @param {number} phoneNumberInformation.hoursUntilExpiration The number of milliseconds from now, after which the phone number is considered expired
 */
function cachePhoneNumber({
  localStorageKey,
  newPhoneNumber,
  newShortCode,
  hoursUntilExpiration,
  currentUserSvocId,
}) {
  if (newPhoneNumber === null || newShortCode === null) return null;

  try {
    const expiration = new Date();
    expiration.setHours(expiration.getHours() + hoursUntilExpiration);

    const serializedPhoneNumberInformation = JSON.stringify({
      phoneNumber: newPhoneNumber,
      shortCode: newShortCode,
      expiration,
      svocId: currentUserSvocId,
    });

    localStorage.setItem(localStorageKey, serializedPhoneNumberInformation);
  } catch (_) {}
}

/**
 * This function replaces the statically generated phone numbers
 * or empty placeholders in the given elements with the given phone number.
 *
 * The static HTML we push to the CDN contains either the default
 * phone number or empty placeholder elements that we can render
 * a phone number into once we have one. So here "replace" means
 * to either overwrite that default phone number, or to add in
 * the given phone number to the empty placeholder elements.
 *
 * The reason for this distinction is that some phone numbers are rendered
 * in the header—above the fold, if you will—and some are rendered in the footer.
 * We don't want to have any kind of flicker in the header as we load in
 * the new phone number, so we decided to make that element an empty placeholder
 * to render the new phone number in once we get it back from Loyalty. The footer
 * phone numbers, in contrast, can be freely overwritten with no concern about flickering.
 *
 * @param {Object} parameters Object containing the below parameters.
 * @param {Object} dynamicPhoneNumberElements The elements to replace phone number information in.
 * @param {Object} dynamicShortCodeElements The elements to replace short code information in.
 * @param {string} newPhoneNumber The number that will be used to replace the default / missing phone number in the given elements.
 * @param {string} newShortCode The number that will be used to replace the default / missing short code in the given elements.
 */
function replacePhoneNumbers({
  dynamicPhoneNumberElements,
  dynamicShortCodeElements,
  newPhoneNumber,
  newShortCode,
}) {
  Object.values(dynamicPhoneNumberElements).forEach(function ({
    elementId,
    modifyElementHtml,
  }) {
    modifyElementHtml({
      phoneNumber: newPhoneNumber,
      currentElement: document.getElementById(elementId),
    });
  });

  Object.values(dynamicShortCodeElements).forEach(function ({
    elementId,
    modifyElementHtml,
  }) {
    modifyElementHtml({
      shortCode: newShortCode,
      currentElement: document.getElementById(elementId),
    });
  });
}

/**
 * Request a phone number from Loyalty that we will use to either:
 * 1. Replace the default phone number in the static HTML from the CDN
 * 2. Add in a phone number for the first time at runtime
 *
 * API documentation can be found here: http://hd-customer-dev.apigee.net/custinfocommons/swagger-ui/index.html#/
 *
 * @returns {Promise} A Promise that resolves to a string for the phone number: either the one we request from loyalty or the default.
 */
function fetchDynamicPhoneNumber(currentUserSvocId) {
  if (!currentUserSvocId) {
    return Promise.resolve({
      newPhoneNumber: defaultPhoneNumber,
      newShortCode: defaultShortCode,
      shouldCache: false,
    });
  }

  const contactNumberRequest = new Request(
    `/cicproxy/benefits/${currentUserSvocId}?params=eliteCallCenterNumber`,
    { credentials: 'omit' },
  );

  return window
    .fetch(contactNumberRequest)
    .then(
      function onResolve(response) {
        /**
         * Fetch treats responses with non-200 status codes as "successful".
         *
         * The Responsen API exposes an `ok` property to check in these
         * instances. So, if the response is not `ok`, that means
         * it was not a 200, and we want to render the default phone number.
         */
        if (!response.ok) {
          return {
            newPhoneNumber: defaultPhoneNumber,
            newShortCode: defaultShortCode,
            shouldCache: false,
          };
        }

        return response.json().then(function (responseData) {
          const {
            eliteCallCenterNumber = defaultPhoneNumber,
            eliteCallCenterSMSCode = defaultShortCode,
          } = responseData;

          return {
            newPhoneNumber: eliteCallCenterNumber,
            newShortCode: eliteCallCenterSMSCode,
            shouldCache: true,
          };
        });
      },
      function onReject(response) {
        /**
         * @todo
         *
         * We might want to consider publishing a failure and/or the provided
         * error message to New Relic.
         *
         * This kind of reporting might also need to wait until we rewrite
         * the HF for Nucleus.
         */
        return {
          newPhoneNumber: defaultPhoneNumber,
          newShortCode: defaultShortCode,
          shouldCache: false,
        };
      },
    )
    .catch(function onError(error) {
      return {
        newPhoneNumber: defaultPhoneNumber,
        newShortCode: defaultShortCode,
        shouldCache: false,
      };
    });
}

module.exports = {
  dynamicPhoneNumberElements,
  dynamicShortCodeElements,
  renderPhoneNumbers,
};
